import React, { useState, useMemo } from 'react';
import { CURRENCY_DENOMS, totalInGp } from '../../lib/inventory';
import { createTradeProposal, offerIsEmpty } from '../../lib/trades';

const EMPTY = () => ({ items: [], currency: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 } });

// One modal that handles both:
//   mode: 'compose' — sender editing a new proposal (all four panes editable)
//   mode: 'review'  — read-only review of an existing pending trade
//                     (recipient: Decline/Accept; sender: Cancel)
export default function TradePanel({
  mode,
  myPlayerId,
  players,
  trade,           // existing trade record in review mode
  onSend,          // (record) => void  — compose mode
  onAccept,        // () => void
  onDecline,       // () => void
  onCancel,        // () => void  (sender cancelling their own pending trade)
  onClose,
}) {
  // ── Compose mode local state ─────────────────────────────────────────────
  const otherPlayers = useMemo(
    () => Object.values(players || {}).filter((p) => p.id !== myPlayerId && p.charName),
    [players, myPlayerId],
  );
  const [recipientId, setRecipientId] = useState(otherPlayers[0]?.id || '');
  const [fromOffer,   setFromOffer]   = useState(EMPTY());
  const [toOffer,     setToOffer]     = useState(EMPTY());

  // ── Resolve sender / recipient + offers depending on mode ────────────────
  const isCompose = mode === 'compose';
  const isReview  = mode === 'review';

  const sender    = isCompose ? players[myPlayerId]   : players[trade?.fromPlayerId];
  const recipient = isCompose ? players[recipientId]  : players[trade?.toPlayerId];
  const senderOffer    = isCompose ? fromOffer : (trade?.fromOffer || EMPTY());
  const recipientOffer = isCompose ? toOffer   : (trade?.toOffer   || EMPTY());

  // Reviewer-perspective: the side viewing this in review mode is always
  // shown what they themselves are offering on the LEFT (matches "you offer")
  // and what the other side is offering on the RIGHT.
  const viewerIsSender = isReview && trade?.fromPlayerId === myPlayerId;
  const viewerOffer    = isReview && !viewerIsSender ? recipientOffer : senderOffer;
  const counterOffer   = isReview && !viewerIsSender ? senderOffer    : recipientOffer;
  const viewerSide     = isReview && !viewerIsSender ? recipient      : sender;
  const counterSide    = isReview && !viewerIsSender ? sender         : recipient;

  const canSend = isCompose
    && !!recipient
    && (!offerIsEmpty(fromOffer) || !offerIsEmpty(toOffer));

  const send = () => {
    const record = createTradeProposal({
      from: sender, to: recipient, fromOffer, toOffer,
    });
    onSend?.(record);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>
            🤝 {isCompose ? 'Propose a Trade' : 'Review Trade'}
          </span>
          {onClose && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
        </div>

        {isCompose && (
          <div style={styles.recipientRow}>
            <span style={styles.recipientLabel}>Trade with</span>
            <select
              style={styles.select}
              value={recipientId}
              onChange={(e) => { setRecipientId(e.target.value); setToOffer(EMPTY()); }}
            >
              {otherPlayers.length === 0 && <option value="">(no other players)</option>}
              {otherPlayers.map((p) => (
                <option key={p.id} value={p.id}>{p.charName}</option>
              ))}
            </select>
          </div>
        )}

        {/* Two columns */}
        <div style={styles.columns}>
          <OfferColumn
            title={isCompose ? 'You offer' : (viewerIsSender ? 'You offered' : `${counterSide?.charName} offers`)}
            player={isCompose ? sender : (isReview && !viewerIsSender ? sender : sender)}
            offer={isCompose ? fromOffer : (viewerIsSender ? viewerOffer : counterOffer)}
            onChange={isCompose ? setFromOffer : null}
            editable={isCompose}
          />
          <OfferColumn
            title={isCompose ? `${recipient?.charName || 'They'} offer${recipient ? 's' : ''}` : (viewerIsSender ? `${recipient?.charName || 'They'} offers` : 'You give')}
            player={isCompose ? recipient : (isReview && !viewerIsSender ? recipient : recipient)}
            offer={isCompose ? toOffer : (viewerIsSender ? counterOffer : viewerOffer)}
            onChange={isCompose ? setToOffer : null}
            editable={isCompose && !!recipient}
          />
        </div>

        {/* Failed reason if any */}
        {isReview && trade?.status === 'failed' && trade?.failedReason && (
          <div style={styles.failedReason}>❌ {trade.failedReason}</div>
        )}

        {/* Action bar */}
        <div style={styles.actions}>
          {isCompose && (
            <>
              <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button style={styles.sendBtn} onClick={send} disabled={!canSend}>
                Send proposal
              </button>
            </>
          )}
          {isReview && trade?.status === 'pending' && viewerIsSender && (
            <>
              <button style={styles.cancelBtn} onClick={onClose}>Close</button>
              <button style={styles.declineBtn} onClick={onCancel}>Cancel proposal</button>
            </>
          )}
          {isReview && trade?.status === 'pending' && !viewerIsSender && (
            <>
              <button style={styles.declineBtn} onClick={onDecline}>Decline</button>
              <button style={styles.sendBtn} onClick={onAccept}>Accept</button>
            </>
          )}
          {isReview && trade?.status !== 'pending' && (
            <button style={styles.cancelBtn} onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Offer column — items + currency, with optional editing ─────────────────────
function OfferColumn({ title, player, offer, onChange, editable }) {
  const [showInventory, setShowInventory] = useState(false);
  const inv = (player?.inventory || []).filter((it) => (it.qty || 0) > 0);

  const updateCurrency = (denom, value) => {
    onChange?.({ ...offer, currency: { ...offer.currency, [denom]: Math.max(0, value | 0) } });
  };
  const addItem = (item) => {
    if (!onChange) return;
    const existing = offer.items.find((s) => s.id === item.id);
    if (existing) {
      // Bump qty if we have more available
      const cap = item.qty || 0;
      const nextQty = Math.min(cap, (existing.qty || 0) + 1);
      onChange({
        ...offer,
        items: offer.items.map((s) => s.id === item.id ? { ...s, qty: nextQty } : s),
      });
    } else {
      onChange({
        ...offer,
        items: [...offer.items, {
          id: item.id, name: item.name, qty: 1, description: item.description || '',
          equipped: !!item.equipped, category: item.category,
        }],
      });
    }
    setShowInventory(false);
  };
  const setItemQty = (slotId, qty) => {
    onChange?.({
      ...offer,
      items: offer.items
        .map((s) => s.id === slotId ? { ...s, qty: Math.max(0, qty | 0) } : s)
        .filter((s) => s.qty > 0),
    });
  };
  const removeItem = (slotId) => {
    onChange?.({ ...offer, items: offer.items.filter((s) => s.id !== slotId) });
  };

  return (
    <div style={styles.column}>
      <div style={styles.colHeader}>
        <span style={{ ...styles.colDot, background: player?.colour || '#9a8b6e' }} />
        <span style={styles.colTitle}>{title}</span>
      </div>

      {/* Items */}
      <div style={styles.colSection}>
        <div style={styles.colSectionLabel}>Items</div>
        {offer.items.length === 0 && <div style={styles.colEmpty}>None</div>}
        {offer.items.map((slot) => {
          const owned = (player?.inventory || []).find((it) => it.id === slot.id);
          const cap   = owned?.qty ?? slot.qty;
          return (
            <div key={slot.id} style={styles.itemSlot}>
              <span style={styles.itemSlotName}>{slot.name}</span>
              {editable ? (
                <span style={styles.qtyMini}>
                  <button style={styles.qtyMiniBtn} onClick={() => setItemQty(slot.id, slot.qty - 1)}>−</button>
                  <span style={styles.qtyMiniVal}>{slot.qty}</span>
                  <button style={styles.qtyMiniBtn} onClick={() => setItemQty(slot.id, Math.min(cap, slot.qty + 1))}>+</button>
                </span>
              ) : (
                <span style={styles.qtyTag}>×{slot.qty}</span>
              )}
              {editable && (
                <button style={styles.removeBtn} onClick={() => removeItem(slot.id)} title="Remove">✕</button>
              )}
            </div>
          );
        })}
        {editable && (
          <>
            {showInventory ? (
              <div style={styles.invList}>
                {inv.length === 0 && <div style={styles.colEmpty}>(empty inventory)</div>}
                {inv.map((it) => (
                  <button key={it.id} style={styles.invRow} onClick={() => addItem(it)}>
                    <span style={styles.invRowName}>{it.name}</span>
                    <span style={styles.invRowQty}>×{it.qty}</span>
                  </button>
                ))}
                <button style={styles.cancelMini} onClick={() => setShowInventory(false)}>Close</button>
              </div>
            ) : (
              <button style={styles.addBtn} onClick={() => setShowInventory(true)}>+ Add item</button>
            )}
          </>
        )}
      </div>

      {/* Currency */}
      <div style={styles.colSection}>
        <div style={styles.colSectionLabel}>Coins</div>
        <div style={styles.coinGrid}>
          {CURRENCY_DENOMS.map((d) => {
            const owned = (player?.currency || {})[d.id] || 0;
            const value = (offer.currency || {})[d.id] || 0;
            return (
              <div key={d.id} style={styles.coinSlot}>
                {editable ? (
                  <>
                    <button style={styles.coinStep} onClick={() => updateCurrency(d.id, value - 1)}>−</button>
                    <input
                      type="number" min="0" max={owned}
                      style={styles.coinInput}
                      value={value}
                      onChange={(e) => updateCurrency(d.id, Math.min(owned, parseInt(e.target.value, 10) || 0))}
                    />
                    <button style={styles.coinStep} onClick={() => updateCurrency(d.id, Math.min(owned, value + 1))}>+</button>
                  </>
                ) : (
                  <span style={styles.coinValueRO}>{value}</span>
                )}
                <span style={styles.coinLabel}>{d.label}</span>
              </div>
            );
          })}
        </div>
        <div style={styles.coinTotalLine}>
          <span style={styles.coinHave}>have ≈ {totalInGp(player?.currency)} gp</span>
          <span style={styles.coinSending}>sending ≈ {totalInGp(offer.currency)} gp</span>
        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  panel:   { width: '100%', maxWidth: 720, maxHeight: '92vh', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: 'rgba(0,0,0,0.2)', flexShrink: 0 },
  title:   { fontSize: 16, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 20, cursor: 'pointer', padding: '0 4px' },

  recipientRow:   { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid rgba(74,61,40,0.5)' },
  recipientLabel: { fontSize: 11, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  select:         { flex: 1, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 13, outline: 'none', fontFamily: 'inherit' },

  columns: { display: 'flex', gap: 8, padding: 10, overflowY: 'auto', flexWrap: 'wrap', flex: '1 1 auto', minHeight: 0 },
  column:  { flex: '1 1 280px', minWidth: 0, background: 'rgba(0,0,0,0.18)', border: '1px solid #4a3d28', borderRadius: 6, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 },
  colHeader: { display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 4, borderBottom: '1px solid rgba(74,61,40,0.5)' },
  colDot:    { width: 10, height: 10, borderRadius: '50%' },
  colTitle:  { fontSize: 12, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em', textTransform: 'uppercase' },

  colSection: { display: 'flex', flexDirection: 'column', gap: 4 },
  colSectionLabel: { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  colEmpty:   { fontSize: 11, color: '#6a5d45', fontStyle: 'italic', padding: '4px 0' },

  itemSlot:   { display: 'flex', alignItems: 'center', gap: 6, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, padding: '4px 6px' },
  itemSlotName:{ flex: 1, fontSize: 12, fontWeight: 700, color: '#f0e6d0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  qtyMini:    { display: 'inline-flex', alignItems: 'center', gap: 2 },
  qtyMiniBtn: { width: 22, height: 22, background: 'transparent', border: '1px solid #4a3d28', borderRadius: 3, color: '#d4a847', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: 0 },
  qtyMiniVal: { minWidth: 22, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#f0e6d0' },
  qtyTag:     { fontSize: 11, fontWeight: 800, color: '#1a0e00', background: '#facc15', borderRadius: 8, padding: '0 6px' },
  removeBtn:  { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 12, cursor: 'pointer', padding: 0, lineHeight: 1 },

  addBtn:    { padding: '6px', background: 'rgba(212,168,71,0.1)', border: '1px dashed #8b7340', borderRadius: 4, color: '#d4a847', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  invList:   { display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto', background: '#0a1426', border: '1px solid #d4a847', borderRadius: 4, padding: 4 },
  invRow:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#f0e6d0', fontSize: 12, cursor: 'pointer', textAlign: 'left' },
  invRowName:{ fontWeight: 700 },
  invRowQty: { fontSize: 11, color: '#9a8b6e' },
  cancelMini:{ background: 'transparent', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', padding: '4px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },

  coinGrid:    { display: 'flex', flexWrap: 'wrap', gap: 4 },
  coinSlot:    { display: 'inline-flex', alignItems: 'center', gap: 2, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, padding: '2px 4px' },
  coinStep:    { width: 22, height: 22, background: 'transparent', border: 'none', color: '#d4a847', fontSize: 14, fontWeight: 800, cursor: 'pointer', padding: 0 },
  coinInput:   { width: 44, background: 'transparent', border: 'none', color: '#f0e6d0', fontSize: 12, fontWeight: 700, outline: 'none', textAlign: 'center' },
  coinValueRO: { minWidth: 24, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#f0e6d0', padding: '0 2px' },
  coinLabel:   { fontSize: 9, color: '#9a8b6e', fontWeight: 700, padding: '0 2px' },
  coinTotalLine: { display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9a8b6e', paddingTop: 2 },
  coinHave:    { color: '#6a5d45' },
  coinSending: { color: '#d4a847', fontWeight: 700 },

  failedReason: { padding: '8px 14px', background: 'rgba(192,51,51,0.15)', borderTop: '1px solid rgba(192,51,51,0.4)', color: '#f87171', fontSize: 12, fontWeight: 700 },

  actions:   { display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '10px 14px', borderTop: '1px solid #4a3d28', background: 'rgba(0,0,0,0.18)', flexShrink: 0 },
  cancelBtn: { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  declineBtn:{ background: 'rgba(192,51,51,0.18)', border: '1px solid rgba(192,51,51,0.5)', borderRadius: 4, color: '#f87171', padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  sendBtn:   { background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', padding: '6px 16px', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4 },
};
